import { valueDiff, getCardSuit } from '../../../Utility/CardsHelper';
import { ICardModifierData } from '../../../Prototype/Level/Card/ICardModifierData';
import BaseCardBehavior from './BaseCardBehavior';
import CardStateHelper, { isCardTypeJoker } from '../CardStateHelper';
import { CardTask } from '../CardTasks';
import CardStateModifierHelper from '../CardStateModifierHelper';
import BaseModifierBehavior from '../ModifierBehavior/BaseModifierBehavior';
import { CardType } from '../../../Prototype/Level/Card/CardType';
import { CardModifierType } from '../../../Prototype/Level/Card/CardModifierType';
import { SCORE_BOMB_CARD, SCORE_FOG_CARD, SCORE_PICKAXE_CARD, SCORE_VALUE_CARD } from '../../../Constants/Score';
import LevelStateHelper from '../../Level/LevelStateHelper';
import LevelHelper from '../../../Utility/LevelHelper';
import { levelCoinCollectMultiplier } from '../../../Constants/Economy';
import { RewardType } from '../../../Utility/RewardHelper';
import { IGameState } from '../../Game/IGameState';
import { ICardsState } from '../ICardState';
import { CardState } from 'Prototype/Level/Card/CardState';
import CheatsManager from 'CheatsManager';
import { InventoryStateHelper } from 'State/Player/Inventory/InventoryStateHelper';

export default class ValueCardBehavior extends BaseCardBehavior {
    public isPlayable(state: ICardsState): boolean {
        const cardState = CardStateHelper.getCardState(state, this.cardID);

        // If a value card is "in-play" it must match the current card (absolute
        // value difference of exactly 1) to be played.
        if (cardState === CardState.IN_PLAY) {
            return (
                super.isPlayable(state) &&
                this.isMatch(state, CardStateHelper.getCurrentCard(state)) &&
                !this.modifiersBlocking(state)
            );
        }

        return super.isPlayable(state);
    }

    public isMatch(state: ICardsState, cardB: string): boolean {
        if (CheatsManager.getFirstInstance()?.shouldIgnoreCardMatchRules()) {
            return true;
        }

        const cardAData = state.cards[this.cardID];
        const cardBData = state.cards[cardB];

        // If nothing to match against always match.
        if (!cardBData) {
            return state.inStack.length === 0;
        }

        // If cardB is a joker always match.
        if (isCardTypeJoker(cardBData.type)) return true;

        if (cardAData.type === CardType.value && cardBData.type === CardType.value) {
            // Check card values are exactly 1 away from eachother (in either direction - wrapping around ACE -> TWO).
            if (valueDiff(cardAData.value, cardBData.value) === 1) {
                return true;
            }
        }

        if (cardAData.type === CardType.value && cardBData.type === CardType.gold) {
            // Check if value card is the same suit as the gold card
            if (cardBData.suit === getCardSuit(cardAData.value)) {
                return true;
            }
        }

        return false;
    }

    public play(oldState: IGameState): IGameState {
        let state = { ...oldState };
        let cardsState = { ...state.levelState.cardsState };
        const currentCardState = CardStateHelper.getCardState(cardsState, this.cardID);

        switch (currentCardState) {
            case CardState.IN_PLAY:
                let coinsCollected: number = 0;
                const modBehaviors = this.modifiersOverriding(cardsState);

                // Modifiers can override the default 'play' method.
                if (modBehaviors.length > 0) {
                    // Modifiers are played instead.
                    for (let i = 0, n = modBehaviors.length; i < n; i++) {
                        state = CardStateModifierHelper.playModifier(
                            state,
                            modBehaviors[i].modifier,
                            modBehaviors[i].cardID
                        );
                    }
                    cardsState = CardStateHelper.getCardsState(state);
                } else {
                    // By default the value card is moved to folded cards (considered a 'valid' turn) and
                    // all modifiers are updated.
                    cardsState = CardStateHelper.pushUndoHistory(cardsState, this.cardID, CardState.IN_PLAY);

                    // If there is a "scoreable" modifier (bomb or fog),
                    // increase streak sequence without increase the streak score.
                    let cardScore = this.getModifiersScore(cardsState);
                    if (cardScore > 0) {
                        cardsState = CardStateHelper.increaseStreak(cardsState, 0, this.cardID);
                    } else {
                        cardsState = CardStateHelper.increaseStreak(cardsState, SCORE_VALUE_CARD, this.cardID);
                        cardScore = cardsState.streakScore;
                    }

                    cardsState = CardStateHelper.addTurnScore(cardsState, cardScore);

                    cardsState = CardStateHelper.setCardFolded(cardsState, this.cardID);

                    state = CardStateHelper.updateGameState(state, cardsState);

                    state = CardStateModifierHelper.updateModifiers(state, this.cardID);

                    if (!LevelStateHelper.checkLevelCompleted(state)) {
                        const levelCost: number = LevelHelper.getLevelCost(state.levelState.levelNumber);
                        const startValue: number = Math.max(Math.round(levelCost * levelCoinCollectMultiplier), 1);

                        coinsCollected = startValue + state.levelState.cardsState.matchSequence.length;

                        state = {
                            ...state,
                            levelState: {
                                ...state.levelState,
                                coinsCollected: state.levelState.coinsCollected + coinsCollected
                            },
                        };
                    }
                    cardsState = CardStateHelper.getCardsState(state);

                    cardsState = CardStateHelper.setStateTaskData(cardsState, {
                        task: CardTask.MATCH,
                        cards: [this.cardID],
                        cardScore: cardScore,
                        coinsCollected,
                        startValue: state.playerState.inventory.coins,
                        finalValue: state.playerState.inventory.coins + coinsCollected,
                    });
                    state = CardStateHelper.updateGameState(state, cardsState);

                    if (coinsCollected) {
                        state = {
                            ...state,
                            playerState: InventoryStateHelper.giveReward(
                                state.playerState,
                                {
                                    type: RewardType.COINS,
                                    value: coinsCollected,
                                },
                                { queueTask: false, forceAnim: true }
                            ),
                        };
                    }
                    cardsState = CardStateHelper.getCardsState(state);

                    cardsState = CardStateHelper.checkStreakBonus(state);

                    cardsState = CardStateHelper.updateCards(cardsState);

                    cardsState = CardStateModifierHelper.onCardPlayed(cardsState, this.cardID);
                }

                break;
            case CardState.IN_STACK:
                if (
                    // if level is FTUX for drawing cards (commented for performance)
                    // state.levelState.tasks.find((e: ILevelStateTaskData) => e.task === LevelTask.FTUX_CARD_DECK) &&
                    // if level is 2
                    state.levelState.levelNumber === 2 &&
                    // if the player hasn't drawn enough cards to follow the tutorial
                    cardsState.folded.length < 6 &&
                    // allow the first stack card to setup the level
                    cardsState.folded.length !== 0
                ) {
                    // block input for level 2 tutorial
                    break;
                }
                state = CardStateHelper.foldStackCard(state, this.cardID);
                cardsState = CardStateHelper.getCardsState(state);
                break;
            case CardState.FOLDED:
            case CardState.DISCARDED:
            case CardState.UNKNOWN:
        }

        return {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState,
            },
        };
    }
}
